/**
 * The UI dictionaries, one chunk per language. A visitor downloads only their own
 * language: the two together were 138 KB, half of the homepage's boot bundle.
 *
 * Loading is asynchronous but rendering is not — `t()` must answer on the first
 * render, identically at prerender and at hydration — so whoever renders first
 * awaits `loadDictionary(lang)`: main.tsx/boot.tsx in the browser, entry-server
 * at build time. After that, `dictionaryFor(lang)` is a plain lookup.
 */
import type { Language } from "@/i18n";

export type Dictionary = Readonly<Record<string, string>>;

const loaders: Record<Language, () => Promise<Dictionary>> = {
  en: () => import("./en").then((mod) => mod.en),
  ar: () => import("./ar").then((mod) => mod.ar),
};

const pending = new Map<Language, Promise<Dictionary>>();
const loaded = new Map<Language, Dictionary>();

/** Starts (once) and resolves the download of one language's dictionary. */
export function loadDictionary(lang: Language): Promise<Dictionary> {
  let promise = pending.get(lang);
  if (!promise) {
    promise = loaders[lang]().then((dictionary) => {
      loaded.set(lang, dictionary);
      return dictionary;
    });
    pending.set(lang, promise);
  }
  return promise;
}

/** The dictionary, if loaded. Rendering before `loadDictionary` resolved is a bug. */
export function dictionaryFor(lang: Language): Dictionary | undefined {
  return loaded.get(lang);
}
